import React, { useState } from 'react';
import { SCHOOL_CONFIG } from '../../config/appConfig';
import useUserStore from '../../store/userStore';
import { LuEye, LuEyeOff, LuLock, LuUser } from 'react-icons/lu';
import Button from '../common/Button';
import Notice from '../common/Notice';
import useSchoolStore from '../../store/schoolStore';
import useGeneralStore from '../../store/generalStore';

const UserInput = () => {
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const {openHelpChatInWhatsapp} = useGeneralStore();

  const { loginUser, isLoading, loginError, clearError } = useUserStore();
  const {getSchoolId} = useSchoolStore();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fullName.trim() || !password.trim()) return;

    // Clear any previous errors
    clearError && clearError();

    // Use school ID from config
    await loginUser(getSchoolId(), fullName.trim(), password);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const hasCredentialError = loginError && loginError.code === 'INVALID_CREDENTIALS';
  const errorControlClass = hasCredentialError ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : '';

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium text-ui-ink-2">Username</span>
          <div className="relative">
            <LuUser aria-hidden className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ui-ink-3" />
            <input
              id="fullName"
              type="text"
              autoComplete="username"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              disabled={isLoading}
              placeholder="Your username"
              className={`ui-control h-10 pl-9 ${errorControlClass}`}
            />
          </div>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium text-ui-ink-2">Password</span>
          <div className="relative">
            <LuLock aria-hidden className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ui-ink-3" />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={isLoading}
              placeholder="Your password"
              className={`ui-control h-10 pr-10 pl-9 ${errorControlClass}`}
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              disabled={isLoading}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              title={showPassword ? 'Hide password' : 'Show password'}
              className="absolute top-1/2 right-1.5 flex size-7 -translate-y-1/2 items-center justify-center rounded-md text-ui-ink-3 transition-colors hover:bg-ui-hover hover:text-ui-ink"
            >
              {showPassword ? <LuEyeOff aria-hidden className="size-4" /> : <LuEye aria-hidden className="size-4" />}
            </button>
          </div>
        </label>

        {loginError && (
          <Notice tone="error" title="Could not sign in">
            {loginError?.message || 'An error occurred during login.'}
          </Notice>
        )}

        <Button
          type="submit"
          size="lg"
          fullWidth
          loading={isLoading}
          disabled={!fullName.trim() || !password.trim()}
          className="mt-1"
        >
          Sign in
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-ui-ink-2">
        Need help signing in?{' '}
        <button
          type="button"
          onClick={() => openHelpChatInWhatsapp(SCHOOL_CONFIG.contact.phone)}
          className="font-medium text-ui-link hover:underline"
        >
          Message {SCHOOL_CONFIG.contact.phone}
        </button>
      </p>
    </div>
  );
};

export default UserInput;
